import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import QuantumFloLogo from '../components/QuantumFloLogo.png';  // ตรวจสอบ path ให้ถูกต้อง
import unifiedOAuthService from '../../../services/brokers/unifiedOAuthService';
import { auth } from '../../../services/firebase';
import BrokerConnectingModal from '../../../components/broker/BrokerConnectingModal';
import BrokerConnectionWarningModal from '../../../components/broker/BrokerConnectionWarningModal';
import { useBrokerIntegration } from '../../../contexts/BrokerIntegrationContextCompat';

export default function BrokerSelect({ onConnectionStatusChange }) {
  const [broker, setBroker] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showConnectingModal, setShowConnectingModal] = useState(false);
  const [connectingBroker, setConnectingBroker] = useState('');
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [pendingBroker, setPendingBroker] = useState('');
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const navigate = useNavigate();

  // Get broker integration data
  const { activeConnections, deleteBrokerIntegration } = useBrokerIntegration();

  const handleConnect = async () => {
    if (!broker) {
      setError('Please select a broker');
      return;
    }

    // Check if user is authenticated
    if (!auth.currentUser) {
      setError('Please log in first before connecting a broker');
      console.error('No authenticated user found');
      return;
    }

    // Check if user already has an active broker connection
    if (activeConnections && activeConnections.length > 0) {
      const currentBroker = activeConnections[0];
      console.log('User already has active broker:', currentBroker.broker);
      
      // Show warning modal
      setPendingBroker(broker);
      setShowWarningModal(true);
      return;
    }

    console.log('Current user:', auth.currentUser.email);
    await proceedWithConnection();
  };

  const proceedWithConnection = async () => {
    setLoading(true);
    setError(null);

    try {
      // For OAuth brokers, use unified service for secure server-side auth
      console.log('Getting auth URL for broker:', broker);
      // Use production mode for now (sandbox credentials not working)
      const isSandbox = false; // broker === 'tradier' ? true : false;
      const authData = await unifiedOAuthService.getAuthorizationUrl(broker, isSandbox);
      console.log('Received auth URL:', authData);
      
      if (authData.url) {
        // Show the connecting modal
        setConnectingBroker(broker);
        setShowConnectingModal(true);
        onConnectionStatusChange(true);
        
        // Delay redirect to show the modal animation
        setTimeout(() => {
          console.log('Redirecting to:', authData.url);
          window.location.href = authData.url;
        }, 1500);
      } else {
        throw new Error('Unable to generate authentication URL');
      }
    } catch (error) {
      console.error('Connection error:', error);
      setError(error.message || 'Failed to connect to broker');
      onConnectionStatusChange(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnectAndConnect = async () => {
    if (!activeConnections || activeConnections.length === 0) return;

    setIsDisconnecting(true);
    setShowWarningModal(false);

    try {
      // Disconnect current broker
      const currentBroker = activeConnections[0];
      console.log('Disconnecting current broker:', currentBroker.broker);
      
      await deleteBrokerIntegration(currentBroker.id);
      
      // Wait a moment for the disconnect to complete
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Proceed with new connection
      setBroker(pendingBroker);
      await proceedWithConnection();
      
    } catch (error) {
      console.error('Error disconnecting broker:', error);
      setError('Failed to disconnect current broker. Please try again.');
    } finally {
      setIsDisconnecting(false);
    }
  };

  const handleCancelWarning = () => {
    setShowWarningModal(false);
    setPendingBroker('');
  };

  const brokerRequirements = {
    tradier: [
      'Minimum of Level 3 Options Approval',
      'Active Margin Account'
    ],
    tastytrade: [
      'Minimum of Basic (margin) options level',
      'Active Margin Account'
    ]
  };

  return (
    <div className="space-y-4 mt-4 relative">
      <div className="broker-logo absolute top-[-130px] right-1">
        <img 
          src={QuantumFloLogo} 
          alt="QuantumFlo Auto Algo Logo" 
          className="w-12 h-12 object-contain"
        />
      </div>

      <h2 className="text-xl text-white font-semibold mb-4 text-center"></h2>

      <select
        value={broker}
        onChange={(e) => setBroker(e.target.value)}
        className="w-full bg-zinc-800 text-white p-3 rounded-md border border-zinc-700"
      >
        <option value="">Select Broker</option>
        <option value="tradier">Tradier</option>
        <option value="tastytrade">Tastytrade</option>
      </select>

      {error && (
        <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded-md mt-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span className="text-sm">{error}</span>
          </div>
        </div>
      )}

      {/* Warning Modal */}
      <BrokerConnectionWarningModal
        isOpen={showWarningModal}
        onClose={handleCancelWarning}
        onDisconnectAndConnect={handleDisconnectAndConnect}
        onCancel={handleCancelWarning}
        currentBroker={activeConnections?.[0]?.broker}
        newBroker={pendingBroker}
        isDisconnecting={isDisconnecting}
      />

      <div className="space-y-3 mt-4">
        <button
          onClick={handleConnect}
          disabled={loading || !broker}
          className={`w-full p-3 rounded-md transition-colors ${
            loading || !broker
              ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
              : 'bg-purple-600 hover:bg-purple-700 text-white'
          }`}
        >
          {loading ? (
            <div className="flex items-center justify-center">
              <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin mr-2"></div>
              Connecting...
            </div>
          ) : (
            'Connect Broker'
          )}
        </button>
        
        <button
          onClick={() => {
            sessionStorage.setItem('allowDashboardNoBroker', '1');
            navigate('/dashboard');
          }}
          className="w-full p-3 rounded-md bg-gray-600 hover:bg-gray-700 text-white transition-colors"
        >
          Cancel
        </button>
      </div>

      {broker && (
        <div className="mt-6 text-xs text-gray-400">
          <p><strong>Requirements:</strong></p>
          <ul className="list-disc ml-4 mt-1 space-y-1">
            {brokerRequirements[broker].map((req, i) => (
              <li key={i}>{req}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Broker Connecting Modal */}
      <BrokerConnectingModal 
        isOpen={showConnectingModal} 
        brokerName={connectingBroker}
      />
    </div>
  );
}
